import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { clear } from '../../lib/forms';
import { Field, Form } from 'react-final-form';
import InputField from '../form-fields/InputField';
import WaitingButton from '../WaitingButton';
import rpc from '../../../common/rpc';
import Button from 'react-bootstrap/Button';
import SuccessLabel from '../SuccessLabel';
import { timeout } from '../../lib/utils';
import { emailValidate } from '../../../common/validations';
import sendEvent from '../../lib/tracker';
import PhoneNumberField from '../form-fields/PhoneNumberField';
import './WhatsAppRequestForm.less';

const RPC = rpc('/api/v1/');

export const VIEW = {
  LOGGED_IN_USER: 'logged_in_user',
  LANDING_SHORT: 'landing_short',
  LANDING_FULL: 'landing_full',
};

function FormHeadline({ view }) {
  const { t } = useTranslation();
  switch (view) {
    case VIEW.LOGGED_IN_USER:
      return '';
    case VIEW.LANDING_SHORT:
      return <p>{t('external.whatsapp.try_it_desc')}</p>;
    case VIEW.LANDING_FULL:
      return <p>{t('external.whatsapp.form_desc')}</p>;
  }
}

function FormFields({ view, country_code }) {
  const { t } = useTranslation();
  switch (view) {
    case VIEW.LOGGED_IN_USER:
    case VIEW.LANDING_SHORT:
      return (
        <Field
          label={t('external.whatsapp.enter_phone')}
          className="WhatsAppRequestForm__input"
          name="phone_number"
          maxLength="20"
          defaultCountry={country_code}
          component={PhoneNumberField}
        />
      );
    case VIEW.LANDING_FULL:
      return (
        <>
          <Field
            label={t('external.whatsapp.name')}
            className="WhatsAppRequestForm__input"
            name="name"
            maxLength="20"
            component={InputField}
            type="text"
          />
          <Field
            label={t('external.whatsapp.phone')}
            className="WhatsAppRequestForm__input"
            name="phone_number"
            maxLength="20"
            defaultCountry={country_code}
            component={PhoneNumberField}
          />
          <Field
            label={t('external.whatsapp.email')}
            className="WhatsAppRequestForm__input"
            name="email"
            maxLength="100"
            component={InputField}
            type="text"
          />
        </>
      );
  }
}

function FormFooterCancelButton({ view, onCancel }) {
  const { t } = useTranslation();
  return view == VIEW.LOGGED_IN_USER ? (
    <Button variant="outline-secondary" className="WhatsAppRequestForm__cancel" onClick={onCancel}>
      {t('global.close')}
    </Button>
  ) : null;
}

function FormFooter({ view, disabled, waiting }) {
  const { t } = useTranslation();
  return (
    <div className="WhatsAppRequestForm__footer">
      <WaitingButton type="submit" disabled={disabled} waiting={waiting}>
        {view == VIEW.LANDING_SHORT
          ? t('external.whatsapp.try_btn')
          : t('external.whatsapp.send_request_btn')}
      </WaitingButton>
    </div>
  );
}

export default function WhatsAppRequestForm(props) {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(props.submitted);
  const class_name = classNames(`WhatsAppRequestForm WhatsAppRequestForm_view-${props.view}`, {
    WhatsAppRequestForm_submitted: submitted,
  });
  const { executeRecaptcha } = useGoogleReCaptcha();
  const onSubmit = useCallback(
    async (values, form) => {
      const errors = {};

      if (!values.phone_number) {
        errors.phone_number = t('validation.no_phone_number');
      }

      if (props.view == VIEW.LANDING_FULL) {
        if (!values.name) {
          errors.name = t('validation.no_name');
        }
        if (!values.email) {
          errors.email = t('validation.no_email');
        } else if (!emailValidate(values.email)) {
          errors.email = t('validation.invalid_email');
        }
      }

      if (Object.keys(errors).length) return errors;

      try {
        const token = await executeRecaptcha('whatsapp_request');
        await RPC('whatsApp.request', {
          ...values,
          captcha: {
            action: 'whatsapp_request',
            token,
          },
        });
        sendEvent('WhatsApp form submitted');
        await timeout(1000);
      } catch (e) {}

      setSubmitted(true);
    },
    [props.view, executeRecaptcha],
  );

  const renderForm = ({ handleSubmit, submitError, submitting, pristine, form }) => (
    <form className={class_name} onSubmit={handleSubmit}>
      <FormHeadline {...props} />
      <div className="WhatsAppRequestForm__form">
        <FormFields {...props} />
        <FormFooter view={props.view} disabled={submitting || pristine} waiting={submitting} />
      </div>
    </form>
  );

  if (submitted) {
    return (
      <div className={class_name}>
        <SuccessLabel text={t('external.whatsapp.success_label')} variant="brand" />
        <FormFooterCancelButton {...props} />
      </div>
    );
  } else {
    return <Form onSubmit={onSubmit} render={renderForm} mutators={{ clear }} />;
  }
}
